/**
 * React Query hook for the A4.3.3 CreativeLibraryPicker.
 *
 * Reads `GET /meta/ad-creatives?meta_ad_account_id=...` — the latest
 * snapshot per creative_id from `meta_ads_data.ad_creatives_daily`.
 * Filter + search happen client-side inside the picker, across all the
 * pages that have been loaded so far.
 *
 * Cursor pagination via `useInfiniteQuery` — the picker renders a
 * "Load more" button when `next_cursor` is non-null. 5-minute
 * staleTime — the table is append-mostly via the daily ingest, so
 * re-fetching on every picker open is wasted.
 *
 * Gated on `hasLinkedMetaAccounts` (from `useAuthStore`) — a member with
 * no Meta credential row has nothing to query, so no request is fired.
 * The old per-UUID beta gate was removed with the free-tier flip; Meta
 * features are broadly available now.
 */
import { useInfiniteQuery } from '@tanstack/react-query';

import { CACHE_CONFIG } from '@/config/constants';
import { metaAdsApi, type MetaAdCreative } from '@/services/api/metaAds';
import { useAuthStore } from '@/store/authStore';

const STALE_TIME_MS = 5 * 60 * 1000;

interface PageData {
  creatives: MetaAdCreative[];
  next_cursor: string | null;
}

export function useMetaAdCreativesQuery(
  metaAdAccountId: string | null | undefined,
) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasLinkedMetaAccounts = useAuthStore((s) => s.hasLinkedMetaAccounts);
  const enabled =
    isAuthenticated &&
    hasLinkedMetaAccounts &&
    !!metaAdAccountId;

  return useInfiniteQuery<PageData>({
    queryKey: ['meta-ad-creatives', metaAdAccountId ?? null],
    queryFn: ({ pageParam }) =>
      metaAdsApi.listAdCreatives(metaAdAccountId as string, {
        after: pageParam as string | null,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    enabled,
    staleTime: STALE_TIME_MS,
    gcTime: CACHE_CONFIG.DEFAULT_DURATION * 2,
    retry: 1,
  });
}
